import { InterpretResponse, MappedCriteria, VoiceCriteria } from "./types";

export const DEFAULT_CONFIDENCE_THRESHOLD = 0.75;

function firstOrUndefined(values: string[] | null | undefined): string | undefined {
  if (!values || values.length === 0) return undefined;
  return values[0];
}

/**
 * Server `criteria` -> this app's form field shape. Defensive against the
 * server sending either `null` or `[]` for "nothing extracted". Only ever
 * returns fields that actually came back non-null — callers must not blank a
 * field the user already filled in by hand just because it's absent here.
 */
export function mapInterpretResponse(
  response: InterpretResponse,
  confidenceThreshold: number = DEFAULT_CONFIDENCE_THRESHOLD,
): MappedCriteria {
  const { criteria, fieldConfidence } = response;

  const targetForm: MappedCriteria["targetForm"] =
    criteria.flightNumber || criteria.airlineName ? "flightNumber" : "route";

  const routeFields: MappedCriteria["routeFields"] = {};
  const from = firstOrUndefined(criteria.source);
  const to = firstOrUndefined(criteria.destination);
  if (from) routeFields.fromLocation = from;
  if (to) routeFields.toLocation = to;
  if (criteria.dates && criteria.dates.length > 0) routeFields.dates = criteria.dates;
  if (criteria.isNearbyDeparture !== null && criteria.isNearbyDeparture !== undefined) {
    routeFields.isNearbyDeparture = criteria.isNearbyDeparture;
  }
  if (criteria.isNearbyArrival !== null && criteria.isNearbyArrival !== undefined) {
    routeFields.isNearbyArrival = criteria.isNearbyArrival;
  }

  const flightNumberFields: MappedCriteria["flightNumberFields"] = {};
  if (criteria.flightNumber) flightNumberFields.flightNumber = criteria.flightNumber;
  if (criteria.airlineName) flightNumberFields.airlineName = criteria.airlineName;

  // leaveTime/arrivalTime never go in routeFields/flightNumberFields — the
  // backend pins their confidence at 0.3 deliberately, precisely so they're
  // never mistaken for an applied filter. Surface them separately instead.
  const timeSuggestions: MappedCriteria["timeSuggestions"] = {};
  if (criteria.leaveTime) timeSuggestions.leaveTime = criteria.leaveTime;
  if (criteria.arrivalTime) timeSuggestions.arrivalTime = criteria.arrivalTime;

  // leaveTime/arrivalTime excluded here on purpose — they're always below
  // threshold by design (see timeSuggestions above), flagging them would be
  // noise on every single successful extraction.
  const flaggedFields = (Object.keys(fieldConfidence) as (keyof VoiceCriteria)[]).filter(
    (field) =>
      field !== "leaveTime" &&
      field !== "arrivalTime" &&
      (fieldConfidence[field] ?? 1) < confidenceThreshold,
  );

  return {
    targetForm,
    routeFields,
    flightNumberFields,
    timeSuggestions,
    flaggedFields,
    candidates: response.candidates ?? null,
    spans: response.spans ?? {},
    reasonCode: response.reasonCode ?? null,
  };
}

/**
 * Merge the user's picks from the AMBIGUOUS_AIRPORT chooser into the partial
 * result — client-side only, no second API call (per spec: "once the user
 * picks, set that slot yourself"). Resolved slots are cleared from
 * candidates/flaggedFields/reasonCode so the result reads as complete.
 */
export function applyCandidateSelections(
  partial: MappedCriteria,
  selections: Partial<Record<keyof VoiceCriteria, string>>,
): MappedCriteria {
  const routeFields = { ...partial.routeFields };
  if (selections.source) routeFields.fromLocation = selections.source;
  if (selections.destination) routeFields.toLocation = selections.destination;

  const resolvedSlots = new Set(Object.keys(selections));

  return {
    ...partial,
    routeFields,
    flaggedFields: partial.flaggedFields.filter((field) => !resolvedSlots.has(field)),
    candidates: null,
    reasonCode: null,
  };
}
